import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  useGetFireDrillQuery,
  useTickFireDrillAttendeeMutation,
  useCompleteFireDrillMutation,
} from '../../store/api/localApi';
import { FireDrillAttendee, FireDrillStatus } from '../../types';
import { useToast } from '../../components/Toast';
import { ScopedLink, useScopedPath } from '../../components/ScopedLink';

// ─── helpers ────────────────────────────────────────────────────────────────

function formatElapsed(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `0:${mm}:${ss}`;
}

function isReconciled(a: FireDrillAttendee): boolean {
  return a.accountedFor || Boolean(a.unaccountedReason);
}

// ─── Unaccounted reason modal ────────────────────────────────────────────────

interface UnaccountedReasonModalProps {
  /** One or more attendees that need a reason filled in. */
  attendees: FireDrillAttendee[];
  drillId: string;
  elapsedSeconds: number;
  startedAtMs: number;
  onDone: () => void;
  onCancel: () => void;
}

const UnaccountedReasonModal: React.FC<UnaccountedReasonModalProps> = ({
  attendees,
  drillId,
  elapsedSeconds,
  startedAtMs,
  onDone,
  onCancel,
}) => {
  const [tick] = useTickFireDrillAttendeeMutation();
  const { showError } = useToast();

  // Map studentId → reason text
  const [reasons, setReasons] = useState<Record<string, string>>(() =>
    Object.fromEntries(attendees.map((a) => [a.studentId, a.unaccountedReason ?? '']))
  );
  const [submitting, setSubmitting] = useState(false);

  const allFilled = attendees.every((a) => reasons[a.studentId]?.trim().length > 0);

  const handleSubmit = async () => {
    if (!allFilled || submitting) return;
    setSubmitting(true);
    try {
      await Promise.all(
        attendees.map((a) =>
          tick({
            drillId,
            studentId: a.studentId,
            accountedFor: false,
            secondsToAccount: Math.floor((Date.now() - startedAtMs) / 1000),
            unaccountedReason: reasons[a.studentId].trim(),
          }).unwrap()
        )
      );
      onDone();
    } catch {
      showError('Failed to save reasons. Please try again.');
      setSubmitting(false);
    }
  };

  // Esc to cancel
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      data-testid="live-drill-unaccounted-reason-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="unaccounted-reason-title"
    >
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75"
          onClick={onCancel}
          aria-hidden="true"
        />

        <div className="relative z-10 bg-white rounded-lg shadow-xl w-full max-w-md p-6">
          <div className="flex items-start justify-between mb-4">
            <h3 id="unaccounted-reason-title" className="text-base font-semibold text-gray-900">
              {attendees.length === 1
                ? `Mark "${attendees[0].studentName}" as unaccounted`
                : `Reason required for ${attendees.length} children`}
            </h3>
            <button
              type="button"
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600 focus:outline-none"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <p className="text-sm text-gray-500 mb-4">
            {attendees.length === 1
              ? 'Enter a reason before marking unaccounted.'
              : 'All children must be reconciled before completing the drill. Enter a reason for each.'}
          </p>

          <div className="space-y-3">
            {attendees.map((a) => (
              <div key={a.studentId}>
                {attendees.length > 1 && (
                  <label
                    htmlFor={`reason-${a.studentId}`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {a.studentName}
                  </label>
                )}
                <input
                  id={`reason-${a.studentId}`}
                  type="text"
                  data-testid="live-drill-unaccounted-reason-input"
                  value={reasons[a.studentId]}
                  onChange={(e) =>
                    setReasons((prev) => ({ ...prev, [a.studentId]: e.target.value }))
                  }
                  placeholder="e.g. Collected by parent before drill"
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                  maxLength={200}
                  autoFocus={attendees[0].studentId === a.studentId}
                />
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-2 mt-5">
            <button
              type="button"
              onClick={onCancel}
              className="px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="live-drill-unaccounted-reason-submit"
              onClick={handleSubmit}
              disabled={!allFilled || submitting}
              className="px-3 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Saving…' : 'Confirm unaccounted'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main screen ─────────────────────────────────────────────────────────────

const LiveDrillScreen: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const scopedPath = useScopedPath();
  const { showError, showSuccess } = useToast();

  const { data: drillData, isLoading } = useGetFireDrillQuery(id!, { skip: !id });
  const drill = drillData?.drill;

  const [tick] = useTickFireDrillAttendeeMutation();
  const [complete, { isLoading: isCompleting }] = useCompleteFireDrillMutation();

  // ── Live timer ──────────────────────────────────────────────────────────────
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!drill || drill.status !== FireDrillStatus.IN_PROGRESS) return;
    const startedAtMs = new Date(drill.startedAt).getTime();
    setElapsed(Math.floor((Date.now() - startedAtMs) / 1000));
    const intervalId = setInterval(
      () => setElapsed(Math.floor((Date.now() - startedAtMs) / 1000)),
      1000
    );
    return () => clearInterval(intervalId);
  }, [drill?.startedAt, drill?.status]);

  // ── Unaccounted reason modal state ──────────────────────────────────────────
  /**
   * `reasonModalAttendees` drives the modal:
   *  - null  → closed
   *  - [one attendee] → opened from tap-to-untick
   *  - [multiple]     → opened from "Complete with gaps" gate
   */
  const [reasonModalAttendees, setReasonModalAttendees] = useState<FireDrillAttendee[] | null>(
    null
  );
  /**
   * When true, closing the reason modal should immediately re-attempt complete.
   */
  const [pendingComplete, setPendingComplete] = useState(false);

  // ── Derived values ───────────────────────────────────────────────────────────
  const headcountAccounted = drill?.attendees.filter((a) => isReconciled(a)).length ?? 0;
  const headcountTotal = drill?.attendees.length ?? 0;
  const headcountReconciled = headcountAccounted === headcountTotal && headcountTotal > 0;

  // ── Tap-to-tick handlers ────────────────────────────────────────────────────
  const handleTapAttendee = async (attendee: FireDrillAttendee) => {
    if (!drill) return;
    const startedAtMs = new Date(drill.startedAt).getTime();

    if (!isReconciled(attendee)) {
      // Unticked → tick as accountedFor: true
      try {
        await tick({
          drillId: drill.id,
          studentId: attendee.studentId,
          accountedFor: true,
          secondsToAccount: Math.floor((Date.now() - startedAtMs) / 1000),
        }).unwrap();
      } catch {
        showError(`Could not account for ${attendee.studentName}. Try again.`);
      }
    } else if (attendee.accountedFor) {
      // Ticked as present → open "Mark unaccounted?" modal
      setReasonModalAttendees([attendee]);
    }
    // If already unaccounted (has reason) — tapping does nothing useful here;
    // use the same untick flow only for accountedFor=true rows.
  };

  const handleMarkAllPresent = async () => {
    if (!drill) return;
    const startedAtMs = new Date(drill.startedAt).getTime();
    const unticked = drill.attendees.filter((a) => !isReconciled(a));
    if (unticked.length === 0) return;
    const secondsToAccount = Math.floor((Date.now() - startedAtMs) / 1000);
    try {
      await Promise.all(
        unticked.map((a) =>
          tick({
            drillId: drill.id,
            studentId: a.studentId,
            accountedFor: true,
            secondsToAccount,
          }).unwrap()
        )
      );
    } catch {
      showError('Some attendees could not be marked present. Please check and retry.');
    }
  };

  const handleComplete = async () => {
    if (!drill || isCompleting) return;

    if (!headcountReconciled) {
      // Gate: surface modal for all un-reconciled kids
      const missing = drill.attendees.filter((a) => !isReconciled(a));
      setPendingComplete(true);
      setReasonModalAttendees(missing);
      return;
    }

    await doComplete();
  };

  const doComplete = async () => {
    if (!drill || isCompleting) return;
    try {
      await complete({ id: drill.id }).unwrap();
      showSuccess('Drill completed.');
      navigate(scopedPath(`/fire-drills/${drill.id}/wrap-up`));
    } catch (err: any) {
      showError(err?.data?.error || 'Could not complete the drill. Please try again.');
    }
  };

  const handleReasonModalDone = async () => {
    setReasonModalAttendees(null);
    if (pendingComplete) {
      setPendingComplete(false);
      // Attempt complete — RTK Query cache will have updated by now
      await doComplete();
    }
  };

  const handleReasonModalCancel = () => {
    setReasonModalAttendees(null);
    setPendingComplete(false);
  };

  // ── Loading / error / wrong-status states ────────────────────────────────────

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">
        Loading drill…
      </div>
    );
  }

  if (!drill) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-sm text-slate-500">
        <p className="text-base font-medium text-slate-700">Drill not found.</p>
        <ScopedLink to="/fire-drills" className="text-primary-600 hover:underline">
          ← Back to fire drills
        </ScopedLink>
      </div>
    );
  }

  if (drill.status === FireDrillStatus.COMPLETED || drill.status === FireDrillStatus.WITHDRAWN) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-sm text-slate-500">
        <p className="text-base font-medium text-slate-700">
          {drill.status === FireDrillStatus.COMPLETED
            ? 'This drill is already completed.'
            : 'This drill has been withdrawn.'}
        </p>
        <ScopedLink to="/fire-drills" className="text-primary-600 hover:underline">
          ← Back to fire drills log
        </ScopedLink>
      </div>
    );
  }

  // ── Main render ──────────────────────────────────────────────────────────────

  const startedAtMs = new Date(drill.startedAt).getTime();

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="live-drill-screen">
      {/* Sticky header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-gray-900 leading-tight">🔥 Live Fire Drill</h1>
            <p className="text-xs text-gray-500 mt-0.5">
              {drill.exitRoute} · {drill.assemblyPoint}
            </p>
          </div>

          {/* Timer */}
          <div className="text-right">
            <div
              data-testid="live-drill-timer"
              className="text-2xl font-mono font-bold text-red-600 tabular-nums"
            >
              {formatElapsed(elapsed)}
            </div>
            <div
              data-testid="live-drill-headcount-summary"
              className="text-xs text-gray-500 mt-0.5"
            >
              {headcountAccounted} of {headcountTotal} accounted for
            </div>
          </div>
        </div>
      </header>

      {/* Attendee list */}
      <main className="flex-1 overflow-y-auto max-w-2xl w-full mx-auto px-4 py-4 space-y-2">
        {drill.attendees.length === 0 && (
          <p className="text-center text-sm text-gray-400 py-10">
            No attendees registered for this drill.
          </p>
        )}

        {drill.attendees.map((attendee) => {
          const reconciled = isReconciled(attendee);
          const presentAndTicked = attendee.accountedFor;
          const hasReason = Boolean(attendee.unaccountedReason);

          return (
            <button
              key={attendee.studentId}
              type="button"
              data-testid={`live-drill-attendee-${attendee.studentId}`}
              onClick={() => handleTapAttendee(attendee)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border text-left transition-colors ${
                presentAndTicked
                  ? 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100'
                  : hasReason
                  ? 'bg-amber-50 border-amber-200 hover:bg-amber-100'
                  : 'bg-white border-gray-200 hover:bg-gray-50'
              }`}
            >
              {/* Status indicator */}
              <span
                data-testid={`live-drill-attendee-${attendee.studentId}-status`}
                className={`flex-shrink-0 h-7 w-7 rounded-full flex items-center justify-center ${
                  presentAndTicked
                    ? 'bg-emerald-500 text-white'
                    : hasReason
                    ? 'bg-amber-500 text-white'
                    : 'border-2 border-gray-300 bg-white'
                }`}
                aria-hidden="true"
              >
                {presentAndTicked && <CheckIcon className="h-4 w-4" />}
                {hasReason && <XMarkIcon className="h-4 w-4" />}
              </span>

              {/* Name + reason */}
              <div className="flex-1 min-w-0">
                <span
                  className={`block text-sm font-medium truncate ${
                    presentAndTicked
                      ? 'text-emerald-900'
                      : hasReason
                      ? 'text-amber-900'
                      : 'text-gray-900'
                  }`}
                >
                  {attendee.studentName}
                </span>
                {hasReason && (
                  <span className="block text-xs text-amber-700 truncate mt-0.5">
                    {attendee.unaccountedReason}
                  </span>
                )}
                {presentAndTicked && attendee.secondsToAccount != null && (
                  <span className="block text-xs text-emerald-600 mt-0.5">
                    {formatElapsed(attendee.secondsToAccount)}
                  </span>
                )}
              </div>

              {/* Tap hint for un-reconciled rows */}
              {!reconciled && (
                <span className="flex-shrink-0 text-xs text-gray-400">Tap to mark present</span>
              )}
            </button>
          );
        })}
      </main>

      {/* Sticky footer actions */}
      <footer className="bg-white border-t border-gray-200 sticky bottom-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            type="button"
            data-testid="live-drill-mark-all-present"
            onClick={handleMarkAllPresent}
            disabled={headcountReconciled}
            className="flex-1 py-2.5 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Mark all present
          </button>

          <button
            type="button"
            data-testid="live-drill-complete"
            onClick={handleComplete}
            disabled={isCompleting}
            className={`flex-1 py-2.5 rounded-md text-sm font-semibold transition-colors ${
              headcountReconciled
                ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                : 'bg-primary-600 text-white hover:bg-primary-700'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isCompleting ? 'Completing…' : 'Complete drill →'}
          </button>
        </div>
      </footer>

      {/* Unaccounted reason modal */}
      {reasonModalAttendees && (
        <UnaccountedReasonModal
          attendees={reasonModalAttendees}
          drillId={drill.id}
          elapsedSeconds={elapsed}
          startedAtMs={startedAtMs}
          onDone={handleReasonModalDone}
          onCancel={handleReasonModalCancel}
        />
      )}
    </div>
  );
};

export default LiveDrillScreen;
